import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from './components/ui/card';
import wordmark from './assets/narratoria-banner.png';
import headphones from './assets/headphones.png';
import openBook from './assets/open-book.png';
import quillAndInkwell from './assets/quill-and-inkwell.png';

// Static branded NARRATORIA home page — gold-on-black, matching the listening
// app's .listening-theme palette (dark background, antique-gold primary) and the
// Cormorant Garamond serif used by SignInScreen and the confirming-rental screen.
//
// Shown behind the temporary /home-preview route while the real flow is built in
// later steps. The Audiobook card is live and routes into the listening app; eBook
// and Publishing Checklist are not active yet (Coming soon, non-tappable).

// The route that renders ListeningApp. We reuse the existing temporary
// /signin-preview route rather than duplicating ListeningApp — ListeningApp shows
// its own sign-in when signed out and the library when signed in, so routing into
// it is all the Audiobook card needs to do.
const LISTENING_APP_ROUTE = '/signin-preview';

// The three brand sections. Audiobook is live; the other two are coming soon and
// carry a slightly muted style.
const SECTIONS = [
  { label: 'Audiobook', monogram: headphones, alt: 'Headphones monogram', live: true },
  { label: 'eBook', monogram: openBook, alt: 'Open book monogram', live: false },
  {
    label: 'Publishing Checklist',
    monogram: quillAndInkwell,
    alt: 'Quill and inkwell monogram',
    live: false,
  },
];

// Navigate the same way the rest of the app does (e.g. ListeningLibrary -> /listen/{id}).
function openListeningApp() {
  window.location.href = LISTENING_APP_ROUTE;
}

function HomeScreen() {
  return (
    // Pure black (#000000) scoped to HomeScreen only — the banner and monogram
    // PNGs have a baked-in black background, so matching the page/cards to true
    // black hides their rectangles. The global .listening-theme dark-slate
    // tokens are left untouched; we just override bg here with bg-black.
    <div className="listening-theme min-h-screen w-full bg-black text-foreground flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        {/* Gold-bordered NARRATORIA wordmark banner (no tagline by design). */}
        <img
          src={wordmark}
          alt="NARRATORIA"
          className="mx-auto w-full"
        />

        {/* Three stacked section cards. */}
        <div className="mt-12 flex flex-col gap-8">
          {SECTIONS.map((section) => (
            <Card
              key={section.label}
              // Pure-black card with a thin, low-opacity antique-gold border so it
              // still reads as a distinct tappable card against the black page.
              // The Audiobook card is the only live one: tappable, with a chevron.
              onClick={section.live ? openListeningApp : undefined}
              role={section.live ? 'button' : undefined}
              tabIndex={section.live ? 0 : undefined}
              onKeyDown={
                section.live
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openListeningApp();
                      }
                    }
                  : undefined
              }
              className={`flex flex-row items-center gap-5 rounded-xl border bg-black p-6 border-[hsl(42_52%_54%)]/25 ${
                section.live ? 'cursor-pointer' : 'opacity-60'
              }`}
            >
              <img
                src={section.monogram}
                alt={section.alt}
                className="h-24 w-24 flex-shrink-0 object-contain"
              />
              {/* The label takes the remaining width and wraps within it between
                  words (min-w-0 lets it shrink below its single-line width); the
                  right-hand marker / chevron sits in a fixed-width gutter (w-10,
                  shrink-0) anchored to the right, so it keeps its spot and can
                  never be pushed off the card or overlapped by a long label. The
                  "Coming soon" marker wraps to two short lines inside that gutter. */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="min-w-0 flex-1 font-display text-3xl font-semibold text-primary">
                  {section.label}
                </span>
                <div className="flex w-10 flex-shrink-0 justify-end">
                  {section.live ? (
                    <ChevronRight
                      className="h-7 w-7 text-primary/70"
                      aria-hidden="true"
                    />
                  ) : (
                    <span className="text-right text-xs uppercase leading-tight tracking-wide text-muted-foreground">
                      Coming soon
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HomeScreen;
